'use client';

import { useState } from 'react';
import BlurImage from '@/components/BlurImage';
import siteTheme from '@/lib/theme';
import type { HomeSectionDarkSlider } from '@/lib/cms';
import ArrowIcon from '@/components/ArrowIcon';
const { fonts } = siteTheme;

export default function DarkSlider({ data }: { data: HomeSectionDarkSlider }) {
  const SLIDES = data.slides.map((s) => ({
    id:    s.id,
    label: s.label,
    img:   s.image?.url ?? '',
    body:  s.body,
    href:  s.href,
  }));
  const topLabel = data.top_label;

  const [current, setCurrent] = useState(0);

  const prev = () => setCurrent((c) => (c - 1 + SLIDES.length) % SLIDES.length);
  const next = () => setCurrent((c) => (c + 1) % SLIDES.length);

  const prevSlide = SLIDES[(current - 1 + SLIDES.length) % SLIDES.length];
  const nextSlide = SLIDES[(current + 1) % SLIDES.length];
  const slide     = SLIDES[current];

  const body = slide.body ?? '';
  const firstLetter = body.charAt(0);
  const restBody    = body.slice(1);

  return (
    <section>

      {/* ══════════════════════════════════════════════════════
          DESKTOP
      ══════════════════════════════════════════════════════ */}
      <div className="hidden lg:block" style={{ position: 'relative', height: 'clamp(60rem, 82vh, 90rem)', overflow: 'hidden' }}>

        {/* ── Slides — full height ── */}
        {SLIDES.map((s, i) => (
          <div key={s.id} style={{
            position: 'absolute', inset: 0,
            opacity: i === current ? 1 : 0,
            transition: 'opacity 0.7s ease',
            pointerEvents: i === current ? 'auto' : 'none',
          }}>
            {/* Only load the active slide and the next one (pre-fetch for fast transition).
                All other slide images are skipped to avoid unnecessary bandwidth. */}
            {s.img && (i === current || i === (current + 1) % SLIDES.length) && (
              <BlurImage src={s.img} alt={s.label} fill
                className="object-cover object-center"
                sizes="100vw" priority={i === 0} />
            )}
          </div>
        ))}

        {/* Dark overlay */}
        <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(5,15,25,0.45)', zIndex: 1 }} />

        {/* TOP CENTER: small label */}
        <div style={{ position: 'absolute', top: 'clamp(3rem, 5vw, 5rem)', left: 0, right: 0, zIndex: 2, textAlign: 'center' }}>
          <p className="card-meta" style={{ fontSize: '1.3rem', letterSpacing: '0.3em', color: 'rgba(255,255,255,0.8)', lineHeight: 2 }}>
            {topLabel}
          </p>
        </div>
        



        {/* SLIDE LABEL — above glass band */}
        <h2 className="section-title" style={{
          position: 'absolute', bottom: 'clamp(8rem, 11vw, 12rem)', left: 0, right: 0, zIndex: 3,
          fontSize: 'clamp(1.2rem, 2vw, 3.5rem)',
          color: '#fff', lineHeight: 0.95,
          textAlign: 'center', textWrap: 'balance' as const,
          transition: 'opacity 0.4s ease',
          padding: '0 clamp(2rem, 6vw, 8rem) clamp(2rem, 3vw, 3rem)',
        }}>
          {slide.label}
        </h2>


        {/* GLASS BAND — title + body + button live here */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 3,
          height: 'clamp(8rem, 11vw, 13rem)',
          backgroundColor: 'rgba(5, 13, 20, 0.65)',
          backdropFilter: 'blur(28px)',
          WebkitBackdropFilter: 'blur(28px)',
          borderTop: '1px solid rgba(255,255,255,0.12)',
          display: 'flex', alignItems: 'center',
          padding: '0 clamp(2rem, 6vw, 8rem)',
        }}>
          <div style={{
            width: '100%', maxWidth: '144rem', margin: '0 auto',
            display: 'grid', gridTemplateColumns: '1fr 3fr 1fr',
            gap: 'clamp(2rem, 4vw, 6rem)', alignItems: 'center',
          }}>

            {/* LEFT: prev */}
            <button onClick={prev} className="btn-luxury-arrow" aria-label="Previous slide" style={{
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '1.2rem',
              padding: 0, textAlign: 'left',
            }}>
              <p className="card-meta" style={{ fontSize: '1.2rem', letterSpacing: '0.26em', color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>
                {prevSlide.label}
              </p>
              <ArrowIcon direction="left" size={40} style={{ color: 'rgba(255,255,255,0.45)' }} />
            </button>

            {/* CENTER: large serif title + body + button */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'clamp(1.6rem, 2.2vw, 2.4rem)' }}>
             
              <p className="section-body" style={{ fontSize: 'clamp(1.3rem, 1.3vw, 1.5rem)', lineHeight: 1.75, color: 'rgba(255,255,255,0.6)', textAlign: 'center', maxWidth: '52rem' }}>
                <span style={{ fontFamily: fonts.script, fontSize: '2.1em', lineHeight: 0.75, verticalAlign: 'baseline', color: 'rgba(255,255,255,0.75)' }}>{firstLetter}</span>
                {restBody}
              </p>
          
            </div>

            {/* RIGHT: next */}
            <button onClick={next} className="btn-luxury-arrow" aria-label="Next slide" style={{
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '1.2rem',
              padding: 0, textAlign: 'right',
            }}>
              <p className="card-meta" style={{ fontSize: '1.2rem', letterSpacing: '0.26em', color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>
                {nextSlide.label}
              </p>
              <ArrowIcon size={40} style={{ color: 'rgba(255,255,255,0.45)' }} />
            </button>

          </div>
        </div>

      </div>

      {/* ══════════════════════════════════════════════════════
          MOBILE
      ══════════════════════════════════════════════════════ */}
      <div className="lg:hidden">
        {/* Full-viewport image */}
        <div style={{ position: 'relative', height: '100svh', minHeight: '56rem', overflow: 'hidden' }}>
          {SLIDES.map((s, i) => (
            <div key={s.id} style={{
              position: 'absolute', inset: 0,
              opacity: i === current ? 1 : 0,
              transition: 'opacity 0.7s ease',
              pointerEvents: i === current ? 'auto' : 'none',
            }}>
              {/* No priority here — desktop branch already preloads slide 0;
                  preloading both creates an unused-preload warning on desktop.
                  Only render active + next slide image to save bandwidth. */}
              {s.img && (i === current || i === (current + 1) % SLIDES.length) && (
                <BlurImage src={s.img} alt={s.label} fill
                  className="object-cover object-center"
                  sizes="100vw" />
              )}
            </div>
          ))}
          <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(5,15,25,0.45)', zIndex: 1 }} />

          {/* TOP: small label */}
          <div style={{
            position: 'absolute', top: 'clamp(3rem, 8vw, 5rem)',
            left: 0, right: 0, zIndex: 2, textAlign: 'center',
            padding: '0 clamp(2rem, 5vw, 4rem)',
          }}>
            <p className="card-meta" style={{ fontSize: '1.2rem', letterSpacing: '0.3em', color: 'rgba(255,255,255,0.75)', lineHeight: 1.8 }}>
              {topLabel}
            </p>
          </div>

          {/* BOTTOM gradient: large serif title */}
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 2,
            padding: '6rem clamp(2rem, 5vw, 4rem) clamp(2.5rem, 5vw, 4rem)',
            background: 'linear-gradient(to top, rgba(5,15,25,0.9) 0%, transparent 100%)',
          }}>
            <h2 className="section-title" style={{ fontSize: 'clamp(3.2rem, 8vw, 5rem)', color: '#fff', lineHeight: 1, marginBottom: '1.6rem', textAlign: 'center', textWrap: 'balance' as const }}>
              {slide.label}
            </h2>
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.25)', width: '100%' }} />
          </div>
        </div>

        {/* Dark bottom band */}
        <div style={{
          backgroundColor: '#0b1c29',
          padding: 'clamp(3rem, 6vw, 5rem) clamp(2rem, 5vw, 4rem)',
        }}>
          {/* Body text */}
          <p className="section-body" style={{ fontSize: 'clamp(1.4rem, 3.5vw, 1.6rem)', lineHeight: 1.8, color: 'rgba(255,255,255,0.5)', textAlign: 'center', marginBottom: 'clamp(2.5rem, 5vw, 3.5rem)' }}>
            <span style={{ fontFamily: fonts.script, fontSize: '2.2em', lineHeight: 0.8, verticalAlign: 'baseline' }}>{firstLetter}</span>
            {restBody}
          </p>


          {/* Navigation */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2.4rem' }}>
            <button onClick={prev} className="btn-luxury-arrow" aria-label="Previous slide" style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              color: 'rgba(255,255,255,0.4)', lineHeight: 1,
            }}>
              <ArrowIcon direction="left" size={32} style={{ color: 'inherit' }} />
            </button>
            <p style={{
              fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 300,
              letterSpacing: '0.15em', color: 'rgba(255,255,255,0.4)',
            }}>
              {current + 1} / {SLIDES.length}
            </p>
            <button onClick={next} className="btn-luxury-arrow" aria-label="Next slide" style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              color: 'rgba(255,255,255,0.4)', lineHeight: 1,
            }}>
              <ArrowIcon size={32} style={{ color: 'inherit' }} />
            </button>
          </div>
        </div>
      </div>

    </section>
  );
}
